import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Copy, Trash2, Image, Search, Grid, List, Check, X, ZoomIn, Download, Link2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { API } from '../../lib/api';

export default function AdminMedia() {
  const { token } = useAuth();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [selectedItems, setSelectedItems] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    fetchMedia();
  }, [token]);

  const fetchMedia = async () => {
    try {
      const res = await axios.get(`${API}/admin/media`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMedia(res.data || []);
    } catch (error) {
      toast.error('Errore caricamento media');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    let successCount = 0;
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('file', file);
      formData.append('nome', file.name);

      try {
        await axios.post(`${API}/admin/media/upload`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        successCount++;
      } catch (error) {
        toast.error(`Errore: ${file.name}`);
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} file caricati`);
      fetchMedia();
    }
    setUploading(false);
  };

  const handleFileSelect = (e) => {
    handleUpload(e.target.files);
    e.target.value = '';
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleUpload(e.dataTransfer.files);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa immagine?')) return;
    
    try {
      await axios.delete(`${API}/admin/media/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Eliminato');
      fetchMedia();
      setSelectedItems(prev => prev.filter(i => i !== id));
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Eliminare ${selectedItems.length} immagini?`)) return;
    
    for (const id of selectedItems) {
      try {
        await axios.delete(`${API}/admin/media/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Error deleting:', id);
      }
    }
    toast.success(`${selectedItems.length} file eliminati`);
    setSelectedItems([]);
    fetchMedia();
  };

  const getAbsoluteUrl = (url) => {
    if (!url) return '';
    let apiUrl = url;
    if (!url.includes('/api/')) {
      apiUrl = url.replace('/uploads/', '/api/uploads/');
    }
    if (apiUrl.includes('/api/api/')) {
      apiUrl = apiUrl.replace('/api/api/', '/api/');
    }
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://booking.lamaisonettepaestum.com';
    return `${origin}${apiUrl}`;
  };

  const getImageSrc = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/uploads/')) return url;
    return url.replace('/uploads/', '/api/uploads/');
  };

  const copyUrl = async (item) => {
    const fullUrl = getAbsoluteUrl(item.url);
    
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopiedId(item.id);
      toast.success('URL copiato!');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = fullUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedId(item.id);
      toast.success('URL copiato!');
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const toggleSelect = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const filteredMedia = media.filter(item => 
    item.nome?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <AdminLayout title="Media Library">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#C5A059] border-t-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Media Library">
      <div className="space-y-4">
        {/* Upload Area */}
        <div
          className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all ${
            dragActive 
              ? 'border-[#C5A059] bg-[#C5A059]/10' 
              : 'border-gray-300 hover:border-[#C5A059]/50 bg-gradient-to-br from-gray-50 to-white'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={uploading}
          />
          <div className="flex flex-col items-center gap-3">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
              dragActive ? 'bg-[#C5A059] text-white' : 'bg-[#C5A059]/10 text-[#C5A059]'
            }`}>
              <Upload className="w-8 h-8" />
            </div>
            <div>
              <p className="font-semibold text-gray-700">
                {uploading ? 'Caricamento in corso...' : 'Trascina le immagini qui'}
              </p>
              <p className="text-sm text-gray-500">oppure clicca per selezionare</p>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-xl p-3 shadow-sm border">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Cerca immagini..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border-0 shadow-none focus-visible:ring-0 text-sm"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">{filteredMedia.length} immagini</span>
            
            <div className="flex border rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-[#C5A059] text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-[#C5A059] text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            {selectedItems.length > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Elimina ({selectedItems.length})
              </Button>
            )}
          </div>
        </div>

        {/* Media Grid/List */}
        {filteredMedia.length === 0 ? (
          <div className="text-center py-16 bg-gray-50 rounded-xl">
            <Image className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Nessuna immagine trovata</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
            {filteredMedia.map((item) => (
              <div
                key={item.id}
                className={`group relative bg-white rounded-xl overflow-hidden shadow-sm border-2 transition-all hover:shadow-lg ${
                  selectedItems.includes(item.id) ? 'border-[#C5A059] ring-2 ring-[#C5A059]/30' : 'border-transparent hover:border-gray-200'
                }`}
              >
                {/* Selection Checkbox */}
                <button
                  onClick={() => toggleSelect(item.id)}
                  className={`absolute top-2 left-2 z-10 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                    selectedItems.includes(item.id) 
                      ? 'bg-[#C5A059] border-[#C5A059] text-white' 
                      : 'bg-white/80 border-gray-300 opacity-0 group-hover:opacity-100'
                  }`}
                >
                  {selectedItems.includes(item.id) && <Check className="w-3 h-3" />}
                </button>

                {/* Image */}
                <div 
                  className="aspect-square cursor-pointer"
                  onClick={() => setPreviewImage(item)}
                >
                  <img
                    src={getImageSrc(item.url)}
                    alt={item.nome}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f3f4f6" width="100%" height="100%"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-size="12">Errore</text></svg>';
                    }}
                  />
                </div>

                {/* Actions Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-white text-xs truncate mb-2">{item.nome}</p>
                  <div className="flex gap-1">
                    <button
                      onClick={() => copyUrl(item)}
                      className={`flex-1 py-1.5 rounded text-xs font-medium flex items-center justify-center gap-1 transition-colors ${
                        copiedId === item.id 
                          ? 'bg-green-500 text-white' 
                          : 'bg-white/90 text-gray-700 hover:bg-white'
                      }`}
                    >
                      {copiedId === item.id ? <Check className="w-3 h-3" /> : <Link2 className="w-3 h-3" />}
                      {copiedId === item.id ? 'Copiato!' : 'Copia URL'}
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-1.5 rounded bg-red-500/90 text-white hover:bg-red-600"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="p-3 text-left text-xs font-semibold text-gray-500 uppercase w-12"></th>
                  <th className="p-3 text-left text-xs font-semibold text-gray-500 uppercase">Anteprima</th>
                  <th className="p-3 text-left text-xs font-semibold text-gray-500 uppercase">Nome</th>
                  <th className="p-3 text-left text-xs font-semibold text-gray-500 uppercase">URL</th>
                  <th className="p-3 text-center text-xs font-semibold text-gray-500 uppercase">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredMedia.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="p-3">
                      <button
                        onClick={() => toggleSelect(item.id)}
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          selectedItems.includes(item.id) 
                            ? 'bg-[#C5A059] border-[#C5A059] text-white' 
                            : 'border-gray-300'
                        }`}
                      >
                        {selectedItems.includes(item.id) && <Check className="w-3 h-3" />}
                      </button>
                    </td>
                    <td className="p-3">
                      <img
                        src={getImageSrc(item.url)}
                        alt={item.nome}
                        className="w-12 h-12 object-cover rounded cursor-pointer"
                        onClick={() => setPreviewImage(item)}
                      />
                    </td>
                    <td className="p-3 text-sm font-medium">{item.nome}</td>
                    <td className="p-3">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600 block truncate max-w-xs">
                        {getAbsoluteUrl(item.url)}
                      </code>
                    </td>
                    <td className="p-3">
                      <div className="flex justify-center gap-1">
                        <Button
                          size="sm"
                          variant={copiedId === item.id ? 'default' : 'outline'}
                          onClick={() => copyUrl(item)}
                          className={copiedId === item.id ? 'bg-green-500 hover:bg-green-600' : ''}
                        >
                          {copiedId === item.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-500 hover:bg-red-50" onClick={() => handleDelete(item.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Image Preview Modal */}
        {previewImage && (
          <div 
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setPreviewImage(null)}
          >
            <button
              className="absolute top-4 right-4 text-white hover:text-gray-300"
              onClick={() => setPreviewImage(null)}
            >
              <X className="w-8 h-8" />
            </button>
            <div className="max-w-5xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
              <img
                src={getImageSrc(previewImage.url)}
                alt={previewImage.nome}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
              <div className="mt-4 flex items-center justify-center gap-3">
                <p className="text-white text-sm">{previewImage.nome}</p>
                <Button
                  size="sm"
                  onClick={() => copyUrl(previewImage)}
                  className="bg-[#C5A059] hover:bg-[#B08D45]"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copia URL
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
